/**
 * @file Membership and workspace settings — the kernel half of the Account & team
 * surface.
 *
 * ## What this module owns
 *
 * Two things, both pure, neither of which touches a database:
 *
 *   1. **Who is in a workspace, folded honestly.** The rows come from
 *      `workspace_memberships` (the authorization table — `_factory/conventions.md`
 *      §Auth), joined to `users` for a name and an email. `countMembersByRole`
 *      folds the row set into the role breakdown the page renders, so the header
 *      figure and the list beside it are one calculation rather than two.
 *
 *   2. **The one workspace setting the code actually reads.** `PRORATION_METHODS`
 *      says what the platform implements, and this module re-exports it rather
 *      than declaring a second list: a picker that offered a method
 *      `readProrationMethod` (./rent-charges.ts) would reject is a control that
 *      silently does nothing.
 *
 * ## What it deliberately does NOT own
 *
 * **Invitations, role changes and removal are BUILT** (./invitations.ts, apps/api
 * routes/members.ts, docs/TEAM.md); this module holds only their shared schemas and
 * messages. `ACCOUNT_NOT_BUILT` names what is still missing (the other settings), and
 * travels in the API payload so the page states it rather than paraphrasing it.
 *
 * **The other §5 settings.** `workspaces.settings` per PLAN.md §5 also names a
 * timezone default, a partial-payment policy, `fee_payer` and late-fee defaults.
 * Nothing in this codebase reads any of them, so offering a control for one would
 * be a fake control — the exact failure this session has been removing from
 * shipped UI. Each has an entry in `ACCOUNT_NOT_BUILT` saying so, and
 * `workspaceSettingsUpdateSchema` has exactly one key, which is what makes
 * exposing a second setting impossible without editing this file.
 *
 * Kernel discipline: this module imports no app code (CLAUDE.md).
 */
import { z } from "zod";
import { WORKSPACE_NAME_MAX_LENGTH, WORKSPACE_ROLE, type WorkspaceRole } from "./constants.js";
import { PRORATION_METHODS, type ProrationMethod } from "./rent-charges.js";

/**
 * The role enum, in the order the reference lists it: `owner | member | tenant |
 * guarantor` (§5). The DB enum was created in this order, so a fold that walks it
 * produces the breakdown in a stable, meaningful order — the people who run the
 * workspace first, the people who live under it last.
 */
export const MEMBERSHIP_ROLE_ORDER: readonly WorkspaceRole[] = WORKSPACE_ROLE;

/** How each role is named to a landlord. A total map, so a new role must be named. */
export const MEMBERSHIP_ROLE_LABELS: Readonly<Record<WorkspaceRole, string>> = {
  owner: "Owner",
  member: "Member",
  tenant: "Tenant",
  guarantor: "Guarantor",
};

/** One sentence per role, saying what holding it lets a person do. */
export const MEMBERSHIP_ROLE_DESCRIPTIONS: Readonly<Record<WorkspaceRole, string>> = {
  owner: "Runs the portfolio. The only role that may change how tenants are billed.",
  member: "Helps manage the portfolio: buildings, leases, money and maintenance.",
  tenant: "Lives in a unit. Not a landlord-side role.",
  guarantor: "Stands behind a tenancy. Not a landlord-side role.",
};

/** The rows a member list is folded from, as this module needs them. */
export interface TeamMemberLike {
  readonly role: WorkspaceRole;
}

/** `{ owner: 1, member: 2 }` — only the roles that actually occur are present. */
export type MembershipRoleCounts = Partial<Record<WorkspaceRole, number>>;

/**
 * Fold member rows into a per-role breakdown.
 *
 * Only roles that OCCUR get an entry, and the result is built in
 * `MEMBERSHIP_ROLE_ORDER` — so a portfolio with no tenant does not render
 * "Tenants 0", which would be a claim about the world rather than about the rows.
 * The same rule the expense, maintenance and turnover breakdowns follow.
 */
export function countMembersByRole(
  members: readonly TeamMemberLike[],
): MembershipRoleCounts {
  const counts: MembershipRoleCounts = {};
  for (const member of members) {
    counts[member.role] = (counts[member.role] ?? 0) + 1;
  }
  return counts;
}

/**
 * The breakdown as an ordered list, so a screen does not have to re-derive the
 * order from the map's insertion sequence.
 */
export function membershipRoleBreakdown(
  members: readonly TeamMemberLike[],
): readonly { readonly role: WorkspaceRole; readonly members: number }[] {
  const counts = countMembersByRole(members);
  return MEMBERSHIP_ROLE_ORDER.filter((role) => (counts[role] ?? 0) > 0).map((role) => ({
    role,
    members: counts[role] ?? 0,
  }));
}

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** One person in the workspace, as the API returns them. */
export interface TeamMemberDto {
  /** The `workspace_memberships` row id — the membership, not the user. */
  readonly membershipId: string;
  readonly userId: string;
  /**
   * From `users.name`. **Nullable**, because the column is: `PLAN.md` §5 calls it
   * NOT NULL, `0001_init.sql:24` makes it nullable, and the column is the truth.
   * A screen must fall back to the email rather than render "null".
   */
  readonly name: string | null;
  readonly email: string;
  readonly role: WorkspaceRole;
  /** When the membership was created — `workspace_memberships.created_at`, ISO. */
  readonly memberSince: string;
}

/** GET /workspaces/:workspaceId/members */
export interface TeamListDto {
  /** True when the list was cut at the server cap: these are the first rows, not all of them. */
  readonly truncated?: true;
  readonly counts: {
    readonly members: number;
    readonly roles: MembershipRoleCounts;
  };
  /** The ordered breakdown, so the header figure and the list cannot disagree. */
  readonly byRole: readonly { readonly role: WorkspaceRole; readonly members: number }[];
  readonly members: readonly TeamMemberDto[];
}

/** One option the settings picker may offer for `settings.proration`. */
export interface ProrationMethodOptionDto {
  readonly value: ProrationMethod;
  readonly label: string;
}

/**
 * GET /workspaces/:workspaceId/settings
 *
 * **This carries one setting, and the shape says so.** `proration` is the only
 * setting key because it is the only setting any code reads. Adding one for a
 * setting nothing consults would be offering a control that changes nothing.
 *
 * `name` is the workspace's own name, carried here as well as on `GET /me` so the
 * page can say which portfolio it is about without a second request — the sidebar
 * switcher is not always visible, and "how this workspace bills" is meaningless
 * without naming it.
 */
export interface WorkspaceSettingsDto {
  /** `workspaces.name` — the portfolio this setting belongs to. */
  readonly name: string;
  /** The configured method, or `null` when the workspace has not chosen one. */
  readonly proration: ProrationMethod | null;
  /**
   * What `readProrationMethod` accepts, straight from `PRORATION_METHODS`. The
   * picker renders these and the validator accepts these, so no control can offer
   * a value the generator would ignore.
   */
  readonly methods: readonly ProrationMethod[];
  readonly options: readonly ProrationMethodOptionDto[];
  /** The label for the "not configured" option — `null` is a real choice. */
  readonly unconfiguredLabel: string;
  /**
   * Why an unconfigured workspace is charged the full period. Present ONLY when
   * `proration` is null: it is an explanation of a state, and showing it beside a
   * configured method would explain something that is not happening.
   */
  readonly unconfiguredReason: string | null;
  /** Settings §5 names that no code reads, and that therefore have no control. */
  readonly notBuilt: readonly AccountCapabilityNoteDto[];
}

/** One capability this page does not have, and what it would need. */
export interface AccountCapabilityNoteDto {
  readonly key: string;
  readonly label: string;
  readonly reason: string;
}

/**
 * The honest list of what Account & team does NOT do.
 *
 * It travels in the API payload rather than living only in the component so the
 * route header, the DTO and the screen are one statement. **Nothing in this list
 * may ever have a control beside it** — an inert button is the lie this list
 * exists to avoid telling.
 */
export const ACCOUNT_NOT_BUILT: readonly AccountCapabilityNoteDto[] = [
  {
    key: "timezone_default",
    label: "A workspace timezone default",
    reason:
      "PLAN.md §5 lists it in settings, but nothing reads it — each property " +
      "carries its own IANA zone and that is the zone rent is generated in. A " +
      "control for a setting no code consults would be a fake control.",
  },
  {
    key: "partial_payment_policy",
    label: "A partial-payment policy",
    reason:
      "PLAN.md §5 lists it in settings, but nothing reads it. Recording a partial " +
      "payment is already possible; a policy slot nothing consults would imply a " +
      "rule that is not applied.",
  },
  {
    key: "fee_payer",
    label: "Who pays the processing fee",
    reason:
      "PLAN.md §5 lists fee_payer in settings, but nothing reads it — the fee math " +
      "(PLAN.md §7.1) and Stripe are not built (T2.2, T2.1). Offering the toggle " +
      "now would change no charge.",
  },
  {
    key: "late_fee_defaults",
    label: "Late-fee defaults",
    reason:
      "PLAN.md §5 lists late-fee defaults in settings, but nothing reads them. The " +
      "rule lives on the lease (leases.late_fee) and T1.4 deliberately defaulted it " +
      "to nothing, because whether there is a fee is founder-owned policy.",
  },
];

/** The proration sentence, matching `NO_PRORATION_METHOD_REASON` in intent. */
export const SETTINGS_NOT_BUILT_NOTE =
  "This page exposes exactly one setting. Every other setting PLAN.md §5 names in " +
  "workspaces.settings is listed as not built, because nothing in this codebase " +
  "reads it yet.";

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

/**
 * PATCH /workspaces/:workspaceId/settings.
 *
 * **The key is required.** A body with no `proration` is refused rather than
 * treated as a no-op: this endpoint has exactly one setting, so a request that
 * names none is a caller mistake, and silently accepting it would report success
 * for a write that never happened.
 *
 * `proration: null` is the explicit "unset / no proration" state — the value that
 * makes an unconfigured workspace reachable on purpose rather than only by
 * default. A string must be one of `PRORATION_METHODS`; anything else is refused
 * with the field path, so a caller is told which value was wrong.
 */
export const workspaceSettingsUpdateSchema = z.object({
  proration: z.union([
    z.enum(PRORATION_METHODS),
    z.null(),
  ]),
});

/**
 * The roles that may reach the LANDLORD surface: every `/workspaces/:id/...` route
 * (`apps/api/src/routes/support.ts` `authorise` defaults to this set), and the SPA's
 * workspace shell (`apps/web/src/components/WorkspaceGate.tsx`, which sends a signed-in
 * person only to a workspace where they hold one of these).
 *
 * Shared, not duplicated: the API refuses a resident with the same list the SPA uses
 * to decide whether to open the shell at all, so the two cannot drift. Same VALUES as
 * `ASSIGNABLE_TEAM_ROLES` below, DIFFERENT meaning — that one is what an owner may
 * hand out, this one is who may look. A future role (a read-only bookkeeper, say)
 * could be in one and not the other.
 *
 * The complement is `RESIDENT_ROLES` in `apps/api/src/authz.ts`; the two partition
 * `WORKSPACE_ROLE`, and `apps/api/src/__tests__/route-contract.test.ts` proves it
 * (the kernel cannot see the API's half).
 */
export const LANDLORD_ROLES: readonly WorkspaceRole[] = ["owner", "member"];

/** Whether a role may reach the landlord surface. One predicate, so no screen re-lists the roles. */
export function isLandlordRole(role: WorkspaceRole): boolean {
  return LANDLORD_ROLES.includes(role);
}

/** The roles an owner may assign to a team member. Resident roles are never assignable here. */
export const ASSIGNABLE_TEAM_ROLES = ["owner", "member"] as const;
export type AssignableTeamRole = (typeof ASSIGNABLE_TEAM_ROLES)[number];

/** PATCH /workspaces/:id/members/:userId. */
export const memberRoleUpdateSchema = z.object({
  role: z.enum(ASSIGNABLE_TEAM_ROLES),
});
export type MemberRoleUpdateBody = z.infer<typeof memberRoleUpdateSchema>;

/** PATCH /workspaces/:id: trimmed first, same bounds as creating a workspace. */
export const workspaceRenameSchema = z.object({
  name: z.string().trim().min(1, "name is required").max(WORKSPACE_NAME_MAX_LENGTH),
});
export type WorkspaceRenameBody = z.infer<typeof workspaceRenameSchema>;

/** PATCH /workspaces/:id response. */
export interface WorkspaceRenamedDto {
  readonly id: string;
  readonly name: string;
}

/** PATCH /workspaces/:id/members/:userId response. */
export interface MemberRoleChangedDto {
  readonly userId: string;
  readonly role: AssignableTeamRole;
}

/** The 409 every refused last-owner change answers with (`code: "last_owner"`). */
export const LAST_OWNER_MESSAGE =
  "A workspace needs at least one owner. Make someone else an owner first, then try again.";

export type WorkspaceSettingsUpdateInput = z.infer<typeof workspaceSettingsUpdateSchema>;
export type WorkspaceSettingsUpdateBody = WorkspaceSettingsUpdateInput;
